import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SmartTemplateService } from '../smart-template.service';
import Swal from 'sweetalert2';

export interface SmartTemplatePart {
  id?: number;
  name: string;
  description?: string;
  type: 'header' | 'content' | 'footer' | 'signature' | 'custom';
  content: string;
  position: 'top' | 'middle' | 'bottom';
  category_id?: number;
  priority: number;
  order_index: number;
  is_active: boolean;
  is_public: boolean;
  is_required: boolean;
  is_repeatable: boolean;
  tags?: string;
  version?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TemplateVariable {
  name: string;
  description: string;
  type?: string;
}

@Component({
  selector: 'app-smart-template-part',
  templateUrl: './smart-template-part.component.html',
  styleUrls: ['./smart-template-part.component.scss']
})
export class SmartTemplatePartComponent implements OnInit {
  editMode = false;
  isSaving = false;
  showPreview = false;
  previewHtml = '';
  editorMode: 'visual' | 'code' = 'visual';

  templatePart: SmartTemplatePart = {
    name: '',
    description: '',
    type: 'content',
    content: '',
    position: 'middle',
    category_id: undefined,
    priority: 2,
    order_index: 0,
    is_active: true,
    is_public: false,
    is_required: false,
    is_repeatable: false,
    tags: '',
    version: '1.0.0'
  };

  templateVariables: TemplateVariable[] = [];
  categories: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<SmartTemplatePartComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private smartTemplateService: SmartTemplateService
  ) {
    if (data && data.partId) {
      this.editMode = true;
      this.loadTemplatePart(data.partId);
    }

    if (data && data.templateId) {
      // If creating a part for a specific template
      this.templatePart.order_index = data.orderIndex || 0;
    }
  }

  ngOnInit(): void {
    this.loadCategories();
    this.initializeCommonVariables();
  }

  /**
   * Load template part for editing
   */
  loadTemplatePart(partId: number): void {
    this.smartTemplateService.getTemplatePart(partId).subscribe(
      (response: any) => {
        if (response.success && response.data) {
          this.templatePart = response.data.part;
          this.templateVariables = response.data.variables || [];
        }
      },
      (error) => {
        console.error('Error loading template part:', error);
        Swal.fire({
          title: 'Eroare',
          text: 'Nu s-a putut încărca partea pentru editare.',
          icon: 'error'
        });
      }
    );
  }

  /**
   * Load categories for part categorization
   */
  loadCategories(): void {
    this.smartTemplateService.getCategories().subscribe(
      (response: any) => {
        if (response.success && response.data) {
          this.categories = response.data;
        }
      },
      (error) => {
        console.error('Error loading categories:', error);
      }
    );
  }

  /**
   * Initialize common variables
   */
  initializeCommonVariables(): void {
    if (!this.editMode) {
      this.templateVariables = [
        { name: '{{data_curenta}}', description: 'Data curentă în format dd/mm/yyyy' },
        { name: '{{nume_utilizator}}', description: 'Numele utilizatorului care generează documentul' },
        { name: '{{organizatie}}', description: 'Numele organizației' }
      ];
    }
  }

  /**
   * Validate form data
   */
  isFormValid(): boolean {
    return !!(this.templatePart.name && 
              this.templatePart.name.trim().length > 0 && 
              this.templatePart.content && 
              this.templatePart.content.trim().length > 0);
  }

  /**
   * Add new variable
   */
  addVariable(): void {
    const newVariable: TemplateVariable = {
      name: '',
      description: ''
    };
    this.templateVariables.push(newVariable);
  }

  /**
   * Remove variable
   */
  removeVariable(index: number): void {
    this.templateVariables.splice(index, 1);
  }

  /**
   * Insert variable into content
   */
  insertVariable(variableName: string): void {
    const formattedVariable = variableName.startsWith('{{') ? variableName : `{{${variableName}}}`;
    
    if (this.editorMode === 'visual') {
      // Insert into visual editor
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textNode = document.createTextNode(formattedVariable);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        this.templatePart.content += formattedVariable;
      }
    } else {
      // Insert into code editor
      this.templatePart.content += formattedVariable;
    }
  }

  /**
   * Insert common variable
   */
  insertCommonVariable(variableType: string): void {
    const variables: { [key: string]: string } = {
      'data_curenta': '{{data_curenta}}',
      'nume_utilizator': '{{nume_utilizator}}',
      'organizatie': '{{organizatie}}'
    };
    
    const variable = variables[variableType];
    if (variable) {
      this.insertVariable(variable);
    }
  }

  /**
   * Toggle editor mode between visual and code
   */
  toggleEditorMode(): void {
    this.editorMode = this.editorMode === 'visual' ? 'code' : 'visual';
  }

  /**
   * Format text in visual editor
   */
  formatText(command: string): void {
    if (this.editorMode === 'visual') {
      document.execCommand(command, false, '');
    }
  }

  /**
   * Handle content change in visual editor
   */
  onContentChange(event: any): void {
    this.templatePart.content = event.target.innerHTML;
  }

  /**
   * Get word count
   */
  getWordCount(): number {
    const text = this.templatePart.content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    return text ? text.split(' ').length : 0;
  }

  /**
   * Get variable count
   */
  getVariableCount(): number {
    const matches = this.templatePart.content.match(/\{\{[^}]+\}\}/g);
    return matches ? matches.length : 0;
  }

  /**
   * Get character count
   */
  getCharacterCount(): number {
    return this.templatePart.content.length;
  }

  /**
   * Preview template part
   */
  previewPart(): void {
    // Replace common variables with sample data for preview
    let previewContent = this.templatePart.content;
    
    previewContent = previewContent.replace(/\{\{data_curenta\}\}/g, new Date().toLocaleDateString('ro-RO'));
    previewContent = previewContent.replace(/\{\{nume_utilizator\}\}/g, 'Ion Popescu');
    previewContent = previewContent.replace(/\{\{organizatie\}\}/g, 'DGASPC Sector 4');
    
    // Replace custom variables with placeholder text
    this.templateVariables.forEach(variable => {
      const variableName = variable.name.replace(/[{}]/g, '');
      const regex = new RegExp(`\\{\\{${variableName}\\}\\}`, 'g');
      previewContent = previewContent.replace(regex, `[${variable.description || variableName}]`);
    });
    
    this.previewHtml = `
      <div class="template-part part-${this.templatePart.type}">
        <div class="part-header-info">
          <h4>${this.templatePart.name}</h4>
          <span class="part-type-badge">${this.getPartTypeLabel(this.templatePart.type)}</span>
        </div>
        <div class="part-content">${previewContent}</div>
      </div>
    `;
    
    this.showPreview = true;
  }

  /**
   * Close preview
   */
  closePreview(): void {
    this.showPreview = false;
    this.previewHtml = '';
  }

  /**
   * Export preview
   */
  exportPreview(): void {
    const blob = new Blob([this.previewHtml], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.templatePart.name.replace(/\s+/g, '_')}_preview.html`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get part type label
   */
  getPartTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'header': 'Header',
      'content': 'Conținut',
      'footer': 'Footer',
      'signature': 'Semnătură',
      'custom': 'Personalizat'
    };
    return labels[type] || type;
  }

  /**
   * Save template part
   */
  savePart(): void {
    if (!this.isFormValid()) {
      Swal.fire({
        title: 'Eroare validare',
        text: 'Vă rugăm să completați toate câmpurile obligatorii.',
        icon: 'error'
      });
      return;
    }

    this.isSaving = true;

    const partData = {
      part: this.templatePart,
      variables: this.templateVariables
    };

    const saveOperation = this.editMode
      ? this.smartTemplateService.updateTemplatePart(this.templatePart.id!, partData)
      : this.smartTemplateService.createTemplatePart(partData);

    saveOperation.subscribe(
      (response: any) => {
        this.isSaving = false;
        if (response.success) {
          Swal.fire({
            title: 'Succes',
            text: `Partea ${this.editMode ? 'actualizată' : 'creată'} cu succes!`,
            icon: 'success'
          }).then(() => {
            this.dialogRef.close({ success: true, data: response.data });
          });
        } else {
          Swal.fire({
            title: 'Eroare',
            text: response.message || 'A apărut o eroare la salvarea părții.',
            icon: 'error'
          });
        }
      },
      (error) => {
        this.isSaving = false;
        console.error('Error saving template part:', error);
        
        let errorMessage = 'A apărut o eroare neașteptată.';
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }
        
        Swal.fire({
          title: 'Eroare',
          text: errorMessage,
          icon: 'error'
        });
      }
    );
  }

  /**
   * Cancel and close dialog
   */
  cancel(): void {
    if (this.hasUnsavedChanges()) {
      Swal.fire({
        title: 'Modificări nesalvate',
        text: 'Aveți modificări nesalvate. Sigur doriți să ieșiți?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Da, ieși',
        cancelButtonText: 'Rămâi'
      }).then((result) => {
        if (result.isConfirmed) {
          this.dialogRef.close({ success: false });
        }
      });
    } else {
      this.dialogRef.close({ success: false });
    }
  }

  /**
   * Check if there are unsaved changes
   */
  private hasUnsavedChanges(): boolean {
    return this.templatePart.name.length > 0 || 
           this.templatePart.content.length > 0 || 
           this.templateVariables.length > 3; // More than the default 3 common variables
  }
}